import React from "react";
import {
  AbsoluteFill,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Sfx } from "../../b2b/ui/Sfx";
import { LOOK } from "../../b2c/look";
import { Words } from "../../b2c/ui/Words";
import { useMerchant } from "../copy";
import type { SceneProps } from "../timing";
import { shot } from "../merchants";
import { useStage } from "../stage";
import { BEAT } from "../timing";
import { LogoTile } from "../ui/Logo";
import { Backdrop, PhotoCard } from "../ui/Photo";

/** Pile offsets (dx, dy, rotation) per card, in card widths. */
const PILE: [number, number, number][] = [
  [-0.05, -0.1, -5],
  [0.06, 0.03, 4],
  [-0.01, 0.15, -1.5],
];

/**
 * Showcase — the brand in its own words: logo tile, the one-line tagline
 * from the brief (grounded in the merchant's description), and up to three
 * of its photos dealt onto a pile, one per two beats (one per beat in the
 * bumper). The blurred room behind follows the top photo, so the frame
 * takes each photo's colours.
 *
 * As the opening scene (bumper), frame 0 is the thumbnail and the frame
 * email clients show for a GIF: logo, tagline and the first photo are
 * already in place, only the next photos are dealt.
 */
export const Showcase: React.FC<SceneProps> = ({ first }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const { m, tagline } = useMerchant();
  const stage = useStage();

  const count = Math.min(3, m.shots.length);
  const every = durationInFrames >= 6 * BEAT ? 2 * BEAT : BEAT;
  const deals = Array.from({ length: count }, (_, i) =>
    Math.round(first ? i * every : 4 + i * every),
  );
  const top = deals.filter((at) => frame >= at - 4).length - 1;

  const wide = stage.shape === "wide";
  const G = {
    tall: { logo: 180, font: 72, cardW: 840, pile: [stage.w / 2, 1030] },
    feed: { logo: 140, font: 62, cardW: 740, pile: [stage.w / 2, 870] },
    square: { logo: 110, font: 54, cardW: 620, pile: [stage.w / 2, 700] },
    wide: { logo: 170, font: 76, cardW: 900, pile: [1330, stage.h / 2 + 10] },
  }[stage.shape];
  const cardW = count === 1 ? G.cardW * 1.06 : G.cardW;

  const logoIn = first
    ? 1
    : spring({
        frame: frame - 2,
        fps,
        config: { damping: 12, stiffness: 200, mass: 0.7 },
      });

  return (
    <AbsoluteFill>
      <Backdrop src={shot(m, Math.max(0, top)).src} />

      {deals.map((at, i) => {
        const [dx, dy, rot] = count === 1 ? [0, 0, -2] : PILE[i];
        return (
          <PhotoCard
            key={i}
            img={shot(m, i)}
            x={G.pile[0] + dx * cardW}
            y={G.pile[1] + dy * cardW}
            width={cardW}
            at={at}
            rotate={rot}
            from={first && i === 0 ? [0, 0] : [i % 2 ? 0.6 : -0.6, 1.2]}
          />
        );
      })}

      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: wide ? "flex-start" : "center",
          gap: G.font * 0.45,
          ...(wide
            ? {
                left: stage.safe.left,
                width: 700,
                top: 0,
                bottom: 0,
                justifyContent: "center",
              }
            : {
                left: stage.safe.left,
                right: stage.safe.right,
                top: stage.safe.top,
              }),
        }}
      >
        <LogoTile m={m} size={G.logo} style={{ scale: logoIn }} />
        <Words
          text={tagline || m.name}
          start={first ? -30 : 8}
          stagger={2}
          fontSize={G.font}
          color={LOOK.white}
          align={wide ? "left" : "center"}
        />
      </div>

      {deals.slice(first ? 1 : 0).map((at) => (
        <Sfx key={at} sound="pop" at={at} volume={0.8} />
      ))}
    </AbsoluteFill>
  );
};
